/**
 * Active dedicated numbers for a tenant.
 *
 * Renders as a precision-table — fixed-width columns, tabular numerics,
 * inline-editable friendly name, set-as-default action, release confirmation.
 */
import { useState } from 'react';
import {
  Button,
  Input,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  cn,
} from '@respectly/ui';
import {
  Star,
  StarOff,
  Trash2,
  PencilLine,
  Check,
  X,
  Phone,
  Loader2,
  AlertTriangle,
  MessageSquare,
  Mic,
  Image as ImageIcon,
  Users,
  Plus,
} from 'lucide-react';
import type { TelnyxNumberSummary } from '@respectly/shared-types';

interface NumberListProps {
  numbers: TelnyxNumberSummary[];
  onSetDefault: (phoneNumberId: string) => Promise<boolean> | boolean;
  onRename: (phoneNumberId: string, friendlyName: string) => Promise<boolean> | boolean;
  onRelease: (phoneNumberId: string) => Promise<boolean> | boolean;
  onProvisionNumber: () => void;
  /** Open the dialog for assigning an already-procured Telnyx number to this tenant. */
  onAssignExisting?: () => void;
  campaignReady: boolean;
}

function formatE164(num: string): string {
  // +14155551234 → +1 (415) 555-1234
  const m = num.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  if (m) return `+1 (${m[1]}) ${m[2]}-${m[3]}`;
  return num;
}

export function NumberList({
  numbers,
  onSetDefault,
  onRename,
  onRelease,
  onProvisionNumber,
  onAssignExisting,
  campaignReady,
}: NumberListProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmRelease, setConfirmRelease] = useState<TelnyxNumberSummary | null>(null);

  const startEdit = (n: TelnyxNumberSummary) => {
    setEditingId(n.id);
    setDraftName(n.friendlyName ?? '');
  };

  const commitEdit = async (id: string) => {
    setBusyId(id);
    await onRename(id, draftName.trim());
    setBusyId(null);
    setEditingId(null);
  };

  const handleRelease = async () => {
    if (!confirmRelease) return;
    setBusyId(confirmRelease.id);
    await onRelease(confirmRelease.id);
    setBusyId(null);
    setConfirmRelease(null);
  };

  if (numbers.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/50 p-10 text-center dark:border-slate-700 dark:bg-slate-900/20">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
          <Phone className="h-5 w-5 text-slate-400" />
        </div>
        <h3 className="mt-4 text-base font-semibold text-slate-900 dark:text-white">
          No dedicated numbers yet
        </h3>
        <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
          Once the campaign is approved you can provision a dedicated 10DLC number. Until then,
          this tenant&apos;s outbound traffic uses the shared platform pool.
        </p>
        <div className="mt-5 flex items-center justify-center gap-2">
          <Button
            size="sm"
            onClick={onProvisionNumber}
            disabled={!campaignReady}
            className="bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
          >
            <Phone className="mr-1.5 h-3.5 w-3.5" />
            {campaignReady ? 'Provision a number' : 'Awaiting campaign approval'}
          </Button>
          {onAssignExisting && (
            <Button
              size="sm"
              variant="outline"
              onClick={onAssignExisting}
              className="border-slate-300 dark:border-slate-700"
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Assign existing
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900/40">
      <div className="flex items-baseline justify-between border-b border-slate-100 px-6 py-4 dark:border-slate-800">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
            Active numbers
          </p>
          <h3 className="mt-0.5 text-base font-semibold text-slate-900 dark:text-white">
            {numbers.length} dedicated {numbers.length === 1 ? 'number' : 'numbers'}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {onAssignExisting && (
            <Button
              size="sm"
              variant="outline"
              onClick={onAssignExisting}
              className="border-slate-300 dark:border-slate-700"
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Assign existing
            </Button>
          )}
          <Button
            size="sm"
            onClick={onProvisionNumber}
            variant="outline"
            className="border-slate-300 dark:border-slate-700"
          >
            <Phone className="mr-1.5 h-3.5 w-3.5" />
            Add another
          </Button>
        </div>
      </div>

      {/* Header row */}
      <div className="grid grid-cols-[2fr_1.5fr_auto_auto_auto] gap-4 border-b border-slate-100 bg-slate-50/60 px-6 py-2 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 dark:border-slate-800 dark:bg-slate-950/40">
        <span>Number</span>
        <span>Friendly name</span>
        <span>Capabilities</span>
        <span className="text-right">Monthly</span>
        <span className="text-right">Actions</span>
      </div>

      <ul className="divide-y divide-slate-100 dark:divide-slate-800">
        {numbers.map((n) => {
          const isEditing = editingId === n.id;
          const isBusy = busyId === n.id;
          const caps = n.capabilities ?? {};
          return (
            <li
              key={n.id}
              className="grid grid-cols-[2fr_1.5fr_auto_auto_auto] items-center gap-4 px-6 py-4 transition-colors hover:bg-slate-50/60 dark:hover:bg-slate-900/40"
            >
              {/* Number */}
              <div className="flex items-center gap-3">
                {n.isDefault && (
                  <span
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-amber-50 text-amber-600 ring-1 ring-amber-200/70 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-800/50"
                    title="Default sender"
                  >
                    <Star className="h-3.5 w-3.5 fill-current" />
                  </span>
                )}
                <div className="min-w-0">
                  <div className="font-mono text-[14px] tabular-nums font-semibold text-slate-900 dark:text-white">
                    {formatE164(n.number)}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] uppercase tracking-wide text-slate-400">
                      {n.type === 'tollfree' ? 'Toll-free' : n.type}
                    </span>
                    {n.isShared && (n.assignmentCount ?? 0) > 1 && (
                      <span
                        className="inline-flex items-center gap-1 rounded-md bg-violet-50 px-1 py-0.5 text-[9px] font-bold uppercase tracking-wide text-violet-700 ring-1 ring-violet-200/60 dark:bg-violet-950/40 dark:text-violet-300 dark:ring-violet-800/50"
                        title={`Shared with ${n.assignmentCount} accounts`}
                      >
                        <Users className="h-2.5 w-2.5" />
                        Shared · {n.assignmentCount}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Friendly name */}
              <div>
                {isEditing ? (
                  <div className="flex items-center gap-1">
                    <Input
                      value={draftName}
                      onChange={(e) => setDraftName(e.target.value)}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') commitEdit(n.id);
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                      className="h-7 text-xs"
                    />
                    <button
                      type="button"
                      onClick={() => commitEdit(n.id)}
                      disabled={isBusy}
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-emerald-600 hover:bg-emerald-50 disabled:opacity-50 dark:hover:bg-emerald-950/40"
                    >
                      <Check className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingId(null)}
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => startEdit(n)}
                    className="group inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 -mx-1.5 -my-1 text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                  >
                    <span className={cn(!n.friendlyName && 'text-slate-400 italic')}>
                      {n.friendlyName || 'Add a label'}
                    </span>
                    <PencilLine className="h-3 w-3 text-slate-400 opacity-0 transition-opacity group-hover:opacity-100" />
                  </button>
                )}
              </div>

              {/* Capabilities */}
              <div className="flex items-center gap-1.5 text-slate-500">
                <CapPill on={!!caps.sms} icon={MessageSquare} label="SMS" />
                <CapPill on={!!caps.mms} icon={ImageIcon} label="MMS" />
                <CapPill on={!!caps.voice} icon={Mic} label="Voice" />
              </div>

              {/* Monthly */}
              <div className="text-right">
                <div className="font-mono text-sm tabular-nums text-slate-700 dark:text-slate-200">
                  ${n.monthlyFee.toFixed(2)}
                </div>
                <div className="text-[10px] uppercase tracking-wide text-slate-400">/ mo</div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-1">
                {!n.isDefault ? (
                  <button
                    type="button"
                    onClick={() => onSetDefault(n.id)}
                    title="Set as default sender"
                    className="flex h-8 w-8 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-amber-600 dark:hover:bg-slate-800"
                  >
                    <StarOff className="h-4 w-4" />
                  </button>
                ) : (
                  <span
                    className="flex h-8 w-8 items-center justify-center rounded-md text-amber-500"
                    title="Default sender"
                  >
                    <Star className="h-4 w-4 fill-current" />
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => setConfirmRelease(n)}
                  title="Release number"
                  disabled={isBusy}
                  className="flex h-8 w-8 items-center justify-center rounded-md text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50 dark:hover:bg-rose-950/40"
                >
                  {isBusy ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Release confirmation */}
      <Dialog open={!!confirmRelease} onOpenChange={(o) => !o && setConfirmRelease(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-rose-500" />
              Release this number?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-600 dark:text-slate-300">
            <p>
              <span className="font-mono text-base font-semibold text-slate-900 dark:text-white">
                {confirmRelease ? formatE164(confirmRelease.number) : ''}
              </span>{' '}
              will be released back to Telnyx and removed from this tenant&apos;s sender pool.
            </p>
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-300">
              Outbound traffic for this tenant will fall back to the shared platform pool until
              another dedicated number is provisioned.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRelease(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRelease}
              disabled={!!busyId}
            >
              {busyId ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-1.5 h-4 w-4" />
              )}
              Release number
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CapPill({
  on,
  icon: Icon,
  label,
}: {
  on: boolean;
  icon: typeof MessageSquare;
  label: string;
}) {
  return (
    <span
      title={`${label}: ${on ? 'enabled' : 'disabled'}`}
      className={cn(
        'inline-flex h-6 items-center gap-1 rounded-md px-1.5 text-[10px] font-bold uppercase tracking-wide ring-1',
        on
          ? 'bg-sky-50 text-sky-700 ring-sky-200/60 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-800/50'
          : 'bg-slate-50 text-slate-300 ring-slate-200 dark:bg-slate-900/40 dark:text-slate-600 dark:ring-slate-800',
      )}
    >
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}
